import React, { useMemo, useRef } from 'react';
import { UserProfile, ClubEvent, Attendance, RankRequirement } from '../../types';
import { calculatePromotionStats, getEligibleRank } from '../../lib/promotion';
import { isZavalaExpired } from '../../lib/utils';
import { Trophy, Printer } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { toast } from 'sonner';

interface DepartmentRosterProps {
  currentUserProfile: UserProfile | null;
  users: UserProfile[];
  events: ClubEvent[];
  attendance: Attendance[];
  ranks: RankRequirement[];
}

export default function DepartmentRoster({ currentUserProfile, users, events, attendance, ranks }: DepartmentRosterProps) {
  const tableRef = useRef<HTMLDivElement>(null);
  
  // Get departments lead manages
  const depts = currentUserProfile?.departments?.length 
    ? currentUserProfile.departments 
    : (currentUserProfile?.department ? [currentUserProfile.department] : []);

  const roster = useMemo(() => {
    if (depts.length === 0) return [];
    const deptsLower = depts.map(d => d.toLowerCase());
    
    return users.filter(u => {
      // Must be active
      if (u.status === 'inactive' || isZavalaExpired(u.zavalaExpireDate)) return false;
      
      const uDepts = u.departments?.map(d => d.toLowerCase()) || (u.department ? [u.department.toLowerCase()] : []);
      return uDepts.some(d => deptsLower.includes(d));
    }).sort((a, b) => a.lastName.localeCompare(b.lastName));
  }, [users, depts]);

  const handlePrint = async () => {
    if (!tableRef.current) return;
    try {
      toast.info('Generating PDF...');
      const canvas = await html2canvas(tableRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#000000'
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.8);
      
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'in',
        format: 'letter'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`department_roster_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF generated successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate PDF');
    }
  };

  if (depts.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-lcars-blue uppercase tracking-widest p-8 text-center border-2 border-lcars-blue/30 rounded-lg">
        UNABLE TO RETRIEVE DEPARTMENT ASSIGNMENT FOR CURRENT OFFICER
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-[var(--background)]">
      <div className="flex justify-between items-center mb-6 border-b-2 border-lcars-gold/30 pb-4">
        <div>
          <h2 className="text-2xl font-bold text-lcars-gold uppercase tracking-tighter">Department Roster</h2>
          <div className="text-lcars-blue text-xs uppercase tracking-widest">
            {depts.join(', ')}
          </div>
        </div>
        <button 
          onClick={handlePrint}
          className="bg-lcars-orange text-black font-bold uppercase tracking-widest text-xs px-4 py-2 rounded-full hover:bg-white transition-colors flex items-center gap-2"
        >
          <Printer className="w-4 h-4" />
          Print Roster
        </button>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 bg-[var(--background)]/50 border border-white/10 rounded-lg p-4" ref={tableRef}>
        <div className="mb-4">
          <h3 className="text-xl font-bold text-[var(--foreground)] uppercase tracking-tighter">Personnel Roster & Activity</h3>
          <p className="text-[var(--foreground)]/50 text-xs uppercase tracking-widest">CONFIDENTIAL • AUTHORIZED CLEARANCE ONLY</p>
        </div>

        <table className="w-full border-collapse">
          <thead>
            <tr className="text-lcars-blue text-[0.7rem] font-bold tracking-widest uppercase">
              <th className="text-left p-3 border-b border-lcars-blue/30">NAME</th>
              <th className="text-left p-3 border-b border-lcars-blue/30">RANK</th>
              <th className="text-left p-3 border-b border-lcars-blue/30">ROLE(S)</th>
              <th className="text-left p-3 border-b border-lcars-blue/30">POINTS</th>
              <th className="text-left p-3 border-b border-lcars-blue/30">EVENTS (12M)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {roster.map(member => {
              const stats = calculatePromotionStats(member, events, attendance);
              return (
                <tr key={member.uid} className="hover:bg-white/5 transition-colors">
                  <td className="p-3">
                    <div className="font-bold text-sm text-[var(--foreground)]">
                      {member.firstName} {member.lastName}
                    </div>
                    <div className="text-[0.65rem] text-[var(--foreground)]/40">{member.email}</div>
                  </td>
                  <td className="p-3">
                    <span className="bg-white/10 text-[var(--foreground)] px-2 py-0.5 rounded font-bold text-[0.65rem] tracking-wider">
                      {ranks.find(r => r.id === member.currentRank || (member.currentRank === 'CDR' && r.id === 'CMDR'))?.shortName || member.currentRank}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1 max-w-[150px]">
                      {member.roles?.map(r => (
                        <span key={r} className="bg-lcars-blue/20 text-lcars-blue px-1.5 py-0.5 rounded text-[0.55rem] uppercase font-bold">
                          {r}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <Trophy className="w-3 h-3 text-lcars-gold" />
                      <span className="font-bold text-sm text-lcars-gold">{stats.totalPoints}</span>
                    </div>
                  </td>
                  <td className="p-3">
                    <span className="font-mono text-sm text-[var(--foreground)]/80">{stats.eventCount}</span>
                  </td>
                </tr>
              );
            })}
            {roster.length === 0 && (
              <tr>
                <td colSpan={5} className="p-8 text-center text-[var(--foreground)]/40 text-sm tracking-widest uppercase">
                  No active personnel assigned to this department
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
